import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TableSortLabel,
  IconButton,
  Dialog,
  Typography
} from '@mui/material';
import ResponsiveDialog from './ResponsiveDialog';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Racquet, Stringing } from '../types';
import CreateRacquetForm from './CreateRacquetsForm';
import { updateRacquet } from '../api';

interface Props {
  racquets: Racquet[];
  stringings: Stringing[];
  onAddNewRacquet: () => void;
  onUpdated: () => void;
}

type SortField = 'brand' | 'model' | 'usage';

export default function RacquetsTable({ racquets, stringings, onAddNewRacquet, onUpdated }: Props) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('brand');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editRacquet, setEditRacquet] = useState<Racquet | null>(null);

  const filtered = racquets
    .map((r) => {
      const usage = stringings.filter((s) => s.racquetId === r.id).length;
      return { ...r, usage };
    })
    .filter(
      (r) =>
        r.brand.toLowerCase().includes(search.toLowerCase()) ||
        r.model.toLowerCase().includes(search.toLowerCase())
    );

  const sorted = [...filtered].sort((a, b) => {
    let aValue: string | number = '';
    let bValue: string | number = '';

    if (sortBy === 'usage') {
      aValue = a.usage;
      bValue = b.usage;
    } else {
      aValue = a[sortBy];
      bValue = b[sortBy];
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleEdit = (racquet: Racquet) => {
    setEditRacquet(racquet);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
        <TextField
          label="Search racquets"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: 1, mr: 2 }}
        />

        <IconButton
          onClick={onAddNewRacquet}
          sx={{
            borderRadius: '50%',
            backgroundColor: '#282c34',
            width: 40,
            height: 40,
            '&:hover': {
              backgroundColor: '#405a52',
              '& svg': {
                transform: 'scale(1.2)',  // Grow icon on hover
              },
            },
            '& svg': {
              transition: 'transform 0.2s ease-in-out',  // Smooth transition
            },
          }}
        >
          <AddIcon sx={{ color: '#fff' }} />
        </IconButton>

      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'brand'}
                  direction={sortBy === 'brand' ? sortOrder : 'asc'}
                  onClick={() => handleSort('brand')}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'model'}
                  direction={sortBy === 'model' ? sortOrder : 'asc'}
                  onClick={() => handleSort('model')}
                >
                  Model
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'usage'}
                  direction={sortBy === 'usage' ? sortOrder : 'asc'}
                  onClick={() => handleSort('usage')}
                >
                  Usage
                </TableSortLabel>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r) => (
              <TableRow
                key={r.id}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(191, 210, 0, 0.1)',  // light lime green hover
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(191, 210, 0, 0.2) !important',  // light lime green selected
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(191, 210, 0, 0.3) !important',  // slightly darker on hover + selected
                  },
                }}
                onClick={() => handleEdit(r)}
              >
                <TableCell>{r.brand}</TableCell>
                <TableCell>{r.model}</TableCell>
                <TableCell>{r.usage}</TableCell>
                <TableCell padding="checkbox" align="right">
                  <IconButton size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ResponsiveDialog
        open={!!editRacquet}
        onClose={() => setEditRacquet(null)}
        maxWidth="sm"
      >
        <Box p={2}>
          <Typography variant="h6" mb={2}>Edit Racquet</Typography>
          {editRacquet && (
            <CreateRacquetForm
              initialBrand={editRacquet.brand}
              initialModel={editRacquet.model}
              onSave={async (data) => {
                await updateRacquet(editRacquet.id, data);
                setEditRacquet(null);
                onUpdated();
              }}
              onCancel={() => setEditRacquet(null)}
            />
          )}
        </Box>
      </ResponsiveDialog>
    </Box>
  );
}
